import { CssBaseline } from '@mui/material';
import Box from '@mui/material/Box';
import { ThemeProvider } from '@mui/material/styles';
import * as History from 'history';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Redirect, Route, Switch, useHistory } from 'react-router-dom';
import Header from '../components/layouts/Header';
import StyledComponents, {
  ScrollTopFab,
} from '../components/ui/StyledComponents';
import {
  ActiveOnlyContextProvider,
  ShowAdditionalInfoContextProvider,
  useApplicationContext,
} from '../hooks/Context';
import { useI18n } from '../hooks/I18n';
import { useData } from '../hooks/UseData';
import AchievementPage from './routes/AchievementPage';
import Container from './routes/Container';

export const ScrollToTop: React.FC = () => {
  const history = useHistory();
  React.useEffect(() => {
    const unregister = history.listen(
      (_location: History.Location, action: History.Action) => {
        // ブラウザの戻ると進むはPOPイベントになるので無視する
        if (action !== 'POP') {
          window.scrollTo(0, 0);
        }
      }
    );
    return () => {
      unregister();
    };
  }, []);
  return <></>;
};

export const Root: React.FC<{ basePath: string }> = ({ basePath }) => {
  // basePathの末尾は/
  const { fetchPathData, fetchRedirectData } = useData();
  const paths = fetchPathData();
  const redirectPaths = fetchRedirectData();
  const routes: React.ReactElement[] = Object.entries(redirectPaths).map(
    ([key, value]) => {
      key = '/' + key;
      value = '/' + value;
      return (
        <Route key={key} path={key} render={() => <Redirect to={value} />} />
      );
    }
  );
  const { faviconPath, theme, headerProps, topPage, aboutPage } =
    useApplicationContext();
  const i18n = useI18n();
  const title = i18n.LABELS.localize('meta_title');
  const description = i18n.LABELS.localize('meta_description');

  return (
    <ThemeProvider theme={theme}>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="icon" href={faviconPath} sizes="any" type="image/svg+xml" />
      </Helmet>
      <CssBaseline />
      <Box sx={{ display: 'flex' }}>
        <Header {...headerProps} />
        <Box
          component="main"
          sx={{
            mt: 10, // 上からのマージン
            mb: 8, // 下からのマージン
            // mx: 2, // ランキングページのボタンが見切れるためコメントアウト
            width: '100%',
            overflowX: 'hidden', // テーブルスクロールさせる
          }}
        >
          <Switch>
            <Route
              path={`${basePath}preview`}
              render={
                // コンポーネントの見た目を確認するためのパス
                () => <StyledComponents />
              }
            />
            <Route
              exact
              path={`${basePath}achievement`}
              render={() => <AchievementPage paths={paths} />}
            />
            <Route path={`${basePath}about`} render={() => aboutPage} />
            <Route exact path={`${basePath}`} render={() => topPage} />
            {
              // 上記のどれも合致しない場合は下が実行される
            }
            <ActiveOnlyContextProvider>
              <ShowAdditionalInfoContextProvider>
                <Container basePath={basePath} parentPath={''} paths={paths} />
              </ShowAdditionalInfoContextProvider>
            </ActiveOnlyContextProvider>
            {routes}
          </Switch>
          <ScrollTopFab />
        </Box>
      </Box>
    </ThemeProvider>
  );
};
